export interface VisualArtifactComponentInfo {
  id: string;
  type: string;
  name: string;
}

export interface CanonicalizedVisualArtifactComponents {
  componentIds: string[];
  componentInfo: VisualArtifactComponentInfo[];
}

export interface SanitizedVisualPromptArtifacts {
  componentIds: string[];
  componentSummary: string[];
}

const HOLDER_COMPONENT_ID_REGEX = /(?:^|[-_])(?:[a-z0-9]+[-_])*holder(?:$|[-_])/i;

export function isHolderComponentId(id: string | undefined | null): boolean {
  if (!id) return false;
  return HOLDER_COMPONENT_ID_REGEX.test(id);
}

function stripNumericIdSuffix(id: string): string {
  return id.replace(/-\d+$/, '');
}

function numericSuffix(id: string): number {
  const match = id.match(/-(\d+)$/);
  return match ? Number(match[1]) : 0;
}

function normalizeSemanticLabel(info: VisualArtifactComponentInfo): string {
  const source = (info.name || stripNumericIdSuffix(info.id)).trim();
  return source.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function areLikelyShadowDuplicates(
  anchor: VisualArtifactComponentInfo,
  candidate: VisualArtifactComponentInfo,
): boolean {
  const anchorLabel = normalizeSemanticLabel(anchor);
  const candidateLabel = normalizeSemanticLabel(candidate);
  if (!anchorLabel || !candidateLabel) return true;
  return anchorLabel === candidateLabel
    || anchorLabel.includes(candidateLabel)
    || candidateLabel.includes(anchorLabel);
}

function chooseArtifactWinner(group: VisualArtifactComponentInfo[]): VisualArtifactComponentInfo {
  return [...group].sort((a, b) => {
    const aBase = stripNumericIdSuffix(a.id);
    const bBase = stripNumericIdSuffix(b.id);
    const aIsCanonical = a.id === aBase ? 1 : 0;
    const bIsCanonical = b.id === bBase ? 1 : 0;
    if (aIsCanonical !== bIsCanonical) return bIsCanonical - aIsCanonical;

    const aSuffix = numericSuffix(a.id);
    const bSuffix = numericSuffix(b.id);
    if (aSuffix !== bSuffix) return aSuffix - bSuffix;

    return a.id.localeCompare(b.id);
  })[0];
}

export function canonicalizePublishedVisualComponentArtifacts(params: {
  componentIds: string[];
  componentInfo: VisualArtifactComponentInfo[];
}): CanonicalizedVisualArtifactComponents {
  const { componentIds, componentInfo } = params;

  const uniqueComponentIds: string[] = [];
  const seenIds = new Set<string>();
  for (const id of componentIds) {
    if (!id || isHolderComponentId(id) || seenIds.has(id)) continue;
    seenIds.add(id);
    uniqueComponentIds.push(id);
  }

  const infoById = new Map<string, VisualArtifactComponentInfo>();
  for (const info of componentInfo) {
    if (!info?.id || isHolderComponentId(info.id) || infoById.has(info.id)) continue;
    infoById.set(info.id, info);
  }

  const byFamily = new Map<string, VisualArtifactComponentInfo[]>();
  for (const id of uniqueComponentIds) {
    const info = infoById.get(id) || { id, type: 'unknown', name: '' };
    const familyKey = `${info.type}:${stripNumericIdSuffix(id)}`;
    if (!byFamily.has(familyKey)) {
      byFamily.set(familyKey, []);
    }
    byFamily.get(familyKey)!.push(info);
  }

  const suppressedIds = new Set<string>();
  for (const group of byFamily.values()) {
    if (group.length < 2) continue;
    const hasSuffixShadow = group.some((info) => info.id !== stripNumericIdSuffix(info.id));
    if (!hasSuffixShadow) continue;

    const winner = chooseArtifactWinner(group);
    for (const candidate of group) {
      if (candidate.id === winner.id) continue;
      if (!areLikelyShadowDuplicates(winner, candidate)) continue;
      suppressedIds.add(candidate.id);
    }
  }

  const canonicalIds = uniqueComponentIds.filter((id) => !suppressedIds.has(id));
  const canonicalInfo = canonicalIds.map((id) => infoById.get(id) || { id, type: 'unknown', name: '' });

  return {
    componentIds: canonicalIds,
    componentInfo: canonicalInfo,
  };
}

function parseComponentSummaryEntries(line: string): { type: string; entries: string[] } | null {
  const match = line.match(/^([^:]+):\s*\[(.*)\]$/);
  if (!match) return null;

  const type = match[1].trim();
  const body = match[2];
  const entries: string[] = [];
  const entryRegex = /([A-Za-z][A-Za-z0-9_-]{2,})(?:\s+\("([^"]*)"\))?/g;
  let entryMatch: RegExpExecArray | null;
  while ((entryMatch = entryRegex.exec(body)) !== null) {
    const [, id, name] = entryMatch;
    if (isHolderComponentId(id)) continue;
    entries.push(name ? `${id} ("${name}")` : id);
  }

  return { type, entries };
}

export function sanitizeVisualArtifactsForDownstreamPrompt(params: {
  componentIds?: string[];
  componentSummary?: string[];
}): SanitizedVisualPromptArtifacts {
  const componentIds = (params.componentIds || []).filter((id) => !isHolderComponentId(id));
  const componentSummary = (params.componentSummary || [])
    .map(parseComponentSummaryEntries)
    .filter((group): group is { type: string; entries: string[] } => Boolean(group))
    .filter((group) => group.entries.length > 0)
    .map((group) => `${group.type}: [${group.entries.join(', ')}]`);

  return {
    componentIds,
    componentSummary,
  };
}
